import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, Typography, TextField, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import * as Icons from '@material-ui/icons';
import { invokeApig } from '../libs/awsLib';

const useStyles = makeStyles((theme) => ({
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tableWrapper: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  emptyMsg: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  orderCell: {
    width: 80,
    textAlign: 'center',
  },
  typeCell: {
    width: 130,
  },
  actionsCell: {
    width: 100,
    textAlign: 'right',
  },
  addRow: {
    marginTop: theme.spacing(2),
  },
}));

function generateId() {
  return `menu_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

const EMPTY_ITEM = () => ({
  id:     generateId(),
  label:  '',
  type:   'page',
  pageId: '',
  slug:   '',
  url:    '',
});

export default function MenuEditorDialog({
  open,
  onClose,
  designId,
  initialMenu,
  listDesigns,
}) {
  const classes = useStyles();

  const [items,      setItems]      = useState([]);
  const [pages,      setPages]      = useState([]);
  const [isLoadingPages, setIsLoadingPages] = useState(false);
  const [isSaving,   setIsSaving]   = useState(false);
  const [error,      setError]      = useState('');

  // ── Al abrir: inicializar items y cargar páginas disponibles ──────────────
  useEffect(() => {
    if (!open) return;
    setItems(initialMenu && initialMenu.length > 0 ? initialMenu.map((i) => ({ ...i })) : []);
    setError('');
    fetchPages();
  }, [open]); // solo al abrir/cerrar el dialog

  const fetchPages = useCallback(async () => {
    setIsLoadingPages(true);
    try {
      const result = await listDesigns();
      if (result.success) {
        setPages(result.pages || []);
      }
    } catch (err) {
      // No crítico — el usuario puede usar URL manual
    } finally {
      setIsLoadingPages(false);
    }
  }, [listDesigns]);

  // ── Helpers de items ───────────────────────────────────────────────────────

  const addItem = () => {
    setItems((prev) => [...prev, EMPTY_ITEM()]);
  };

  const updateItem = (id, field, value) => {
    setItems((prev) => prev.map((item) => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: value };
      // Al seleccionar una página, autocompletar slug
      if (field === 'pageId' && value) {
        const page = pages.find((p) => p.pageId === value);
        if (page) {
          updated.slug  = page.slug  || '';
          updated.label = updated.label || page.title || page.slug || '';
        }
      }
      return updated;
    }));
  };

  const deleteItem = (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const moveUp = (index) => {
    if (index === 0) return;
    setItems((prev) => {
      const copy = [...prev];
      [copy[index - 1], copy[index]] = [copy[index], copy[index - 1]];
      return copy;
    });
  };

  const moveDown = (index) => {
    setItems((prev) => {
      if (index >= prev.length - 1) return prev;
      const copy = [...prev];
      [copy[index], copy[index + 1]] = [copy[index + 1], copy[index]];
      return copy;
    });
  };

  // ── Guardar ────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setError('');

    // Validar que todos los items tengan label y destino
    for (const item of items) {
      if (!item.label.trim()) {
        setError('Todos los ítems deben tener un nombre');
        return;
      }
      if (item.type === 'page' && !item.pageId) {
        setError(`El ítem "${item.label}" debe tener una página seleccionada`);
        return;
      }
      if (item.type === 'url' && !item.url.trim()) {
        setError(`El ítem "${item.label}" debe tener una URL`);
        return;
      }
    }

    try {
      setIsSaving(true);
      const response = await invokeApig({
        path:   '/design',
        method: 'PUT',
        body:   { designId, menu: items },
      });
      if (response.Status === 0 || response.statusCode === 200) {
        onClose(items);
      } else {
        setError(response.message || 'Error al guardar el menú');
      }
    } catch (err) {
      setError('Error al guardar el menú: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onClose={() => onClose(null)} maxWidth="md" fullWidth>
      <DialogTitle disableTypography>
        <div className={classes.titleRow}>
          <Typography variant="h6">Configuración de Menú</Typography>
          <IconButton size="small" onClick={() => onClose(null)}>
            <Icons.Close />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Define los ítems del menú de navegación de este diseño. Puedes enlazar páginas del diseño o URLs externas.
        </Typography>

        <Paper variant="outlined" className={classes.tableWrapper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.orderCell}>Orden</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell className={classes.typeCell}>Tipo</TableCell>
                <TableCell>Destino</TableCell>
                <TableCell className={classes.actionsCell}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className={classes.emptyMsg}>
                    No hay ítems en el menú. Agrega uno con el botón de abajo.
                  </TableCell>
                </TableRow>
              )}
              {items.map((item, index) => (
                <TableRow key={item.id}>

                  {/* Orden */}
                  <TableCell className={classes.orderCell}>
                    <Tooltip title="Subir">
                      <span>
                        <IconButton size="small" onClick={() => moveUp(index)} disabled={index === 0}>
                          <Icons.ArrowUpward fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Bajar">
                      <span>
                        <IconButton size="small" onClick={() => moveDown(index)} disabled={index === items.length - 1}>
                          <Icons.ArrowDownward fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>

                  {/* Nombre */}
                  <TableCell>
                    <TextField
                      value={item.label}
                      onChange={(e) => updateItem(item.id, 'label', e.target.value)}
                      placeholder="Ej: Inicio"
                      size="small"
                      fullWidth
                      inputProps={{ maxLength: 60 }}
                    />
                  </TableCell>

                  {/* Tipo */}
                  <TableCell className={classes.typeCell}>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={item.type}
                        onChange={(e) => updateItem(item.id, 'type', e.target.value)}
                      >
                        <MenuItem value="page">Página</MenuItem>
                        <MenuItem value="url">URL externa</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>

                  {/* Destino */}
                  <TableCell>
                    {item.type === 'page' ? (
                      <FormControl size="small" fullWidth>
                        <Select
                          value={item.pageId}
                          onChange={(e) => updateItem(item.id, 'pageId', e.target.value)}
                          displayEmpty
                          disabled={isLoadingPages}
                        >
                          <MenuItem value="" disabled>
                            {isLoadingPages ? 'Cargando páginas…' : 'Selecciona una página'}
                          </MenuItem>
                          {pages.map((p) => (
                            <MenuItem key={p.pageId} value={p.pageId}>
                              {p.title || p.slug}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        value={item.url}
                        onChange={(e) => updateItem(item.id, 'url', e.target.value)}
                        placeholder="https://..."
                        size="small"
                        fullWidth
                        inputProps={{ maxLength: 500 }}
                      />
                    )}
                  </TableCell>

                  {/* Eliminar */}
                  <TableCell className={classes.actionsCell}>
                    <Tooltip title="Eliminar ítem">
                      <IconButton size="small" onClick={() => deleteItem(item.id)} style={{ color: '#f44336' }}>
                        <Icons.Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>

                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        <Button
          variant="outlined"
          startIcon={<Icons.Add />}
          onClick={addItem}
          className={classes.addRow}
        >
          Agregar ítem
        </Button>

        {error && (
          <Alert severity="error" style={{ marginTop: 16 }}>{error}</Alert>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '8px 24px 16px' }}>
        <Button onClick={() => onClose(null)} disabled={isSaving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          style={{ backgroundColor: '#00b31f', color: '#fff' }}
          onClick={handleSave}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <Icons.Save />}
        >
          {isSaving ? 'Guardando…' : 'Guardar menú'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
